"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseDefinition = exports.defaultFeatures = exports.parseResponse = exports.validDateOrUndefined = exports.parseDataPoints = void 0;
var comparison_1 = require("./features/comparison");
var metricValue_1 = require("./features/metricValue");
var threshold_1 = require("./features/threshold");
var formatParser_1 = require("./formatting/formatParser");
var models_1 = require("./models");
var DataTable_1 = require("./models/DataTable");
var EffectiveDefinition_1 = require("./models/EffectiveDefinition");
var Result_1 = require("./models/Result");
var allTimeDateWindow_1 = require("./features/window/types/allTimeDateWindow");
var window_1 = require("./features/window");
var parseDataPoints = function (points, maxPrecision) {
    if (maxPrecision === void 0) { maxPrecision = 0.01; }
    if (!points) {
        return [];
    }
    return points.map(function (x) { return parseDataPoint(x, maxPrecision); }).filter(function (point) { return point !== undefined; });
};
exports.parseDataPoints = parseDataPoints;
var parseDataPoint = function (point, maxPrecision) {
    var timestamp = (0, exports.validDateOrUndefined)(point.timestamp);
    if (!timestamp) {
        return undefined;
    }
    var measure = point.measure;
    if (measure === undefined || measure === null || typeof measure !== EffectiveDefinition_1.NumberType) {
        return undefined;
    }
    var measureFactor = 1 / maxPrecision;
    return {
        measure: Math.round(measure * measureFactor) / measureFactor,
        timestamp: timestamp,
    };
};
var timezoneOffsetRegex = /[+-]\d\d:?(\d\d)?$/;
// This function will preserve existing timezone offsets on dates, and otherwise
// will use a regex to deconstruct the `dateString` and use a JS local date constructor.
// `dateString` _must_ take the form of a ISO8601 DateTime string in "extended" form
// (with or without tz) in order to be correctly parsed.
// Ex: "2020-01-01T00:00:00" for 12 AM on January 1, 2020
var dateWithTheRightTimezone = function (dateString) {
    if (dateString.endsWith('Z')) {
        return new Date(dateString);
    }
    if (timezoneOffsetRegex.test(dateString)) {
        // There is already a timezone offset.
        return new Date(dateString);
    }
    if (isNaN(new Date(dateString).getTime())) {
        return undefined;
    }
    var _a = dateString.split('T'), date = _a[0], time = _a[1];
    var _b = date === null || date === void 0 ? void 0 : date.split('-').map(function (x) { return parseInt(x, 10); }), year = _b[0], month = _b[1], day = _b[2];
    var _c = time === null || time === void 0 ? void 0 : time.split(':').map(function (x) { return parseFloat(x); }), hour = _c[0], minute = _c[1], second = _c[2];
    var _d = second
        .toString()
        .split('.')
        .map(function (x) { return parseInt(x, 10); }), s = _d[0], ms = _d[1];
    return new Date(year, month - 1, day, hour, minute, s, ms !== null && ms !== void 0 ? ms : 0);
};
var validDateOrUndefined = function (dateString) {
    if ((0, DataTable_1.isNullOrUndefined)(dateString)) {
        return undefined;
    }
    var result = dateWithTheRightTimezone(dateString);
    if (result === undefined || isNaN(result.getTime())) {
        return undefined;
    }
    return result;
};
exports.validDateOrUndefined = validDateOrUndefined;
// Using `any` here because types coming off the wire could look like anything!
// tslint:disable-next-line: no-any
var parseResponse = function (response) {
    if (response === null || response === undefined) {
        return (0, Result_1.Err)(new Result_1.MetricsEngineError('Response must not be null or undefined'));
    }
    if (typeof response !== 'object' || Array.isArray(response)) {
        return (0, Result_1.Err)(new Result_1.MetricsEngineError('Response must be an object'));
    }
    return parseDefinition(response).map(function (definition) { return ({
        data: (0, exports.parseDataPoints)(response === null || response === void 0 ? void 0 : response.points, (0, formatParser_1.maxPrecision)((0, metricValue_1.getMeasureFormatString)(definition))),
        definition: definition,
        dataRefreshedTimestamp: (0, exports.validDateOrUndefined)(response.dataRefreshedTimestamp),
    }); });
};
exports.parseResponse = parseResponse;
exports.defaultFeatures = {
    measure: { column: metricValue_1.MEASURE_COLUMN_NAME },
    timestamp: { column: metricValue_1.TIMESTAMP_COLUMN_NAME },
};
// tslint:disable-next-line: no-any
function parseDefinition(response) {
    var schema = response.schema;
    var features = response.features;
    var actualSchema = __assign({}, schema);
    var actualFeatures = __assign(__assign({}, exports.defaultFeatures), features);
    var timestampColumnRef = (0, metricValue_1.getTimestampFeature)(actualFeatures);
    var measureColumnRef = (0, metricValue_1.getMeasureFeature)(actualFeatures);
    var granularity = (0, metricValue_1.getTimestampGranularity)({ schema: actualSchema, features: actualFeatures });
    if ((features === null || features === void 0 ? void 0 : features.threshold) === undefined) {
        actualSchema = __assign(__assign({}, schema), (0, comparison_1.defaultComparisonSchema)(timestampColumnRef, measureColumnRef, granularity));
        actualFeatures.threshold = { thresholds: [(0, threshold_1.defaultThresholdFeature)()] };
    }
    if (features === null || features === void 0 ? void 0 : features.dateWindow) {
        actualFeatures.dateWindow = parseDateWindowFeature(features === null || features === void 0 ? void 0 : features.dateWindow, granularity);
    }
    return (0, Result_1.Ok)({
        schema: actualSchema,
        features: actualFeatures,
    });
}
exports.parseDefinition = parseDefinition;
// tslint:disable-next-line: no-any
var parseDateWindowFeature = function (dateWindowFeatureResponse, granularity) {
    var parsedDateWindows = parseDateWindows(dateWindowFeatureResponse === null || dateWindowFeatureResponse === void 0 ? void 0 : dateWindowFeatureResponse.dateWindows).filter(function (item) { return item.isOk(); });
    var dateWindows = parsedDateWindows.length > 0
        ? parsedDateWindows.map(function (dateWindow) { return dateWindow.unwrap(); })
        : [(0, window_1.defaultDateWindow)((0, models_1.dateTruncFromGranularity)(granularity))];
    return { dateWindows: dateWindows };
};
// tslint:disable-next-line: no-any
var parseDateWindows = function (dateWindowListResponse) {
    // tslint:disable-next-line: no-any
    return dateWindowListResponse.map(function (dateWindowResponse) { return parseDateWindow(dateWindowResponse); });
};
// tslint:disable-next-line: no-any
var parseDateWindow = function (dateWindowResponse) {
    switch (dateWindowResponse === null || dateWindowResponse === void 0 ? void 0 : dateWindowResponse.type) {
        case window_1.DateWindowTypes.LATEST_DATA: {
            return latestDataOrLastNWindow(dateWindowResponse);
        }
        case window_1.DateWindowTypes.DEFINED_START: {
            var date = (0, exports.validDateOrUndefined)(dateWindowResponse === null || dateWindowResponse === void 0 ? void 0 : dateWindowResponse.startDate);
            return date
                ? (0, Result_1.Ok)((0, window_1.createDefinedStartDateWindow)(date))
                : (0, Result_1.Err)(new Result_1.MetricsEngineError('Could not parse date'));
        }
        case allTimeDateWindow_1.SpecialDateWindowType.ALL_TIME: {
            return (0, Result_1.Ok)(allTimeDateWindow_1.ALL_TIME_DATE_WINDOW);
        }
        default:
            return (0, Result_1.Err)(new Result_1.MetricsEngineError("Could not parse date window type ".concat(dateWindowResponse === null || dateWindowResponse === void 0 ? void 0 : dateWindowResponse.type)));
    }
};
// tslint:disable-next-line: no-any
var latestDataOrLastNWindow = function (dateWindowResponse) {
    var _a, _b, _c;
    var granularity = (_b = (_a = dateWindowResponse === null || dateWindowResponse === void 0 ? void 0 : dateWindowResponse.duration) === null || _a === void 0 ? void 0 : _a.granularity) !== null && _b !== void 0 ? _b : models_1.DateGranularity.UNKNOWN;
    var numberOfUnits = (_c = dateWindowResponse === null || dateWindowResponse === void 0 ? void 0 : dateWindowResponse.duration) === null || _c === void 0 ? void 0 : _c.numberOfUnits;
    if (granularity !== models_1.DateGranularity.UNKNOWN && numberOfUnits) {
        return (0, Result_1.Ok)((0, window_1.createRelativeWindow)({
            unit: granularity,
            numberOfUnits: numberOfUnits,
        }));
    }
    else if (numberOfUnits) {
        return (0, Result_1.Ok)((0, window_1.createLastNWindow)(numberOfUnits));
    }
    return (0, Result_1.Err)(new Result_1.MetricsEngineError('Could not parse latest data or last n date window'));
};
//# sourceMappingURL=parseDataPoints.js.map